import { apiRequest, ApiRequestError } from '../lib/httpClient'
import { pickBool, pickNumber, pickString } from './shared/coercion'
import type { GuideScope } from '@/features/advisory-ai/assistant/types'

/**
 * Preppy feedback — see PREPPY-FEEDBACK-SPEC.md §2 for the shipped contract.
 *
 * Two endpoints and no polling, so this is a much smaller adapter than
 * `advisoryPopups.ts`. It shares that file's two facts about the API: requests
 * bind case-insensitively so what we send can stay camelCase, and responses come
 * back PascalCase with null fields omitted rather than sent as null.
 *
 * One difference worth knowing: `LatestRatedAt` DOES carry a `Z`, because this
 * table uses `timestamptz`. No manual UTC coercion here, unlike pop-ups.
 */

const BASE = 'advisoryai/feedback'

/** Reads a property under either casing, since the API mixes the two. */
function prop(source: unknown, name: string): unknown {
  if (!source || typeof source !== 'object') return undefined
  const record = source as Record<string, unknown>
  const pascal = name.charAt(0).toUpperCase() + name.slice(1)
  return record[name] ?? record[pascal]
}

export type FeedbackVerdictInput = {
  featureKey: string
  verdict: 'up' | 'down'
}

/**
 * Where the user was when they said it (spec §3).
 *
 * Ids only — never figures, org names or agenda text. This is what turns
 * "2 stars and a sentence" into something a product team can act on.
 */
export type FeedbackContext = {
  scope: GuideScope
  orgId?: string
  callId?: string
}

export type SubmitFeedbackInput = {
  clientId: string
  /** 1–5. Omitted entirely when they only left a comment. */
  rating?: number
  text?: string
  contactOk: boolean
  verdicts?: FeedbackVerdictInput[]
  context: FeedbackContext
}

/** Matches the server's own cap so nobody loses a paragraph to a 400. */
export const FEEDBACK_TEXT_MAX = 2000

export async function submitFeedback(input: SubmitFeedbackInput): Promise<void> {
  const text = input.text?.trim()
  const verdicts = input.verdicts?.filter((v) => v.featureKey && v.verdict) ?? []

  await apiRequest<void>(BASE, {
    method: 'POST',
    query: { clientId: input.clientId },
    // Optional fields are omitted rather than sent empty: the server stores a
    // blank `text` as NULL either way, but sending only what was actually filled
    // in keeps the stored row honest about what the person did.
    body: {
      ...(input.rating ? { rating: input.rating } : {}),
      ...(text ? { text } : {}),
      contactOk: input.contactOk,
      ...(verdicts.length ? { verdicts } : {}),
      context: {
        scope: input.context.scope,
        ...(input.context.orgId ? { orgId: input.context.orgId } : {}),
        ...(input.context.callId ? { callId: input.context.callId } : {}),
      },
    },
  })
}

export type FeedbackStatus = {
  hasRated: boolean
  /** Absent — not null — for someone who has only ever left a comment. */
  rating: number | null
  ratedAt: string | null
}

export async function fetchFeedbackStatus(clientId: string): Promise<FeedbackStatus> {
  const payload = await apiRequest<unknown>(`${BASE}/me`, {
    method: 'GET',
    query: { clientId },
  })

  const rating = prop(payload, 'latestRating')
  const ratedAt = pickString(prop(payload, 'latestRatedAt'))

  return {
    hasRated: pickBool(prop(payload, 'hasRated')),
    // The key is absent rather than null when they have never rated, so a
    // missing value has to stay null instead of collapsing to pickNumber's 0 —
    // "you rated 0/5" would be a fabricated opinion.
    rating: rating === undefined || rating === null ? null : pickNumber(rating),
    ratedAt: ratedAt || null,
  }
}

/**
 * The reason a submission was refused, in words worth showing.
 *
 * This controller answers failures with a **plain-text body**, not a structured
 * error object — so the message is already human-readable and is far more use
 * than "something went wrong". The rate-limit and validation replies in
 * particular tell the user something they can act on.
 */
export function feedbackErrorMessage(error: unknown): string {
  const fallback = 'Could not send that just now. Your feedback is still here — try again in a moment.'
  if (!(error instanceof ApiRequestError)) return fallback

  // Only 400s carry a reason worth repeating. `Request failed` is httpClient's
  // own placeholder for a body it could not read, so showing it would be
  // pretending the server said something it did not.
  const message = error.message.trim()
  if (error.status !== 400 || !message || message === 'Request failed') return fallback
  return message
}
